import { NextPage } from "next";
import { useTranslation } from "next-i18next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import React from "react";

import { CTA } from "@components/CTA";
import Footer from "@components/footer";
import { CaseStudies } from "@components/landing/CaseStudies";
import { HeroCard } from "@components/landing/HeroCard";
import { Process } from "@components/landing/Process";
import { Referals } from "@components/landing/Referals";
import {
  Specializations,
  SpecializationInlineData,
  SpecializationSectionData,
} from "@components/landing/Specializations";
import { Subheader } from "@components/landing/Subheader";
import { TechnologiesSection } from "@components/landing/Technologies";
import { TechnologiesBrag } from "@components/landing/TechnologiesBrag";
import { Values, ValueItemProps } from "@components/landing/Values/Values";
import { Menu } from "@components/menu";
import { PageMetadata } from "@components/PageMetadata";
import { AppContext } from "@context/context";
import { useMediaQueryBreakpoint } from "@hooks";

import landingContentEn from "./landing.en.yaml";
import landingContentPl from "./landing.pl.yaml";

const AuditAnimation = "/animations/audit.json";
const ConsultingAnimation = "/animations/consulting.json";
const DebtAnimation = "/animations/debt.json";
const E2EAnimation = "/animations/e2e.json";
const IterationAnimation = "/animations/iterations.json";
const UXAnimation = "/animations/uxui.json";

interface PageBodyProps {
  locale: string;
}

const PageBody = ({ locale }: PageBodyProps) => {
  const { isTablet } = useMediaQueryBreakpoint();
  const landingContent = locale === "pl" ? landingContentPl : landingContentEn;

  const technologies = [
    "React",
    "Next.js",
    "Material Design",
    "Node.js",
    "Java",
    "Spring",
    "React Native",
    "Kubernetes",
    "Zephyr RTOS",
    "Bluetooth LE",
    "Auracast",
    "SQL",
  ];

  const valuesData: ValueItemProps[] = [
    {
      icon: E2EAnimation,
      title: landingContent.values.e2e.title,
      body: landingContent.values.e2e.body,
    },
    {
      icon: AuditAnimation,
      title: landingContent.values.audit.title,
      body: landingContent.values.audit.body,
    },
    {
      icon: UXAnimation,
      title: landingContent.values.ux.title,
      body: landingContent.values.ux.body,
    },
    {
      icon: DebtAnimation,
      title: landingContent.values.debt.title,
      body: landingContent.values.debt.body,
    },
    {
      icon: ConsultingAnimation,
      title: landingContent.values.consulting.title,
      body: landingContent.values.consulting.body,
    },
    {
      icon: IterationAnimation,
      title: landingContent.values.iteration.title,
      body: landingContent.values.iteration.body,
    },
  ];

  const specializationsInlineData: SpecializationInlineData[] = [
    {
      splineCodeSrc: "/landing/blue.splinecode",
      fallbackSrc: "/landing/blue_fallback.png",
      colorKey: "embedded",
      text: landingContent.specializations.subheader_2,
    },
    {
      splineCodeSrc: "/landing/purple.splinecode",
      fallbackSrc: "/landing/purple_fallback.png",
      colorKey: "automation",
      text: landingContent.specializations.subheader_3,
    },
    {
      splineCodeSrc: "/landing/green.splinecode",
      fallbackSrc: "/landing/green_fallback.png",
      colorKey: "agile",
      text: landingContent.specializations.subheader_5,
    },
  ];

  const specializationsSectionsData: SpecializationSectionData[] = [
    {
      colorKey: "embedded",
      ctaText: landingContent.specializations.embedded.cta,
      seeMoreHref: "/iot",
      header: {
        splineCodeSrc: "/landing/blue.splinecode",
        fallbackSrc: "/landing/blue_fallback.png",
        title: landingContent.specializations.embedded.title,
        description: landingContent.specializations.embedded.description,
        seeMoreText: landingContent.specializations.embedded.see_more,
      },
      contents: [
        {
          header: landingContent.specializations.embedded.content_1.header,
          body: landingContent.specializations.embedded.content_1.body,
        },
        {
          header: landingContent.specializations.embedded.content_2.header,
          body: landingContent.specializations.embedded.content_2.body,
        },
        {
          header: landingContent.specializations.embedded.content_3.header,
          body: landingContent.specializations.embedded.content_3.body,
        },
        {
          header: landingContent.specializations.embedded.content_4.header,
          body: landingContent.specializations.embedded.content_4.body,
        },
      ],
    },
    {
      colorKey: "automation",
      ctaText: landingContent.specializations.automation.cta,
      header: {
        splineCodeSrc: "/landing/purple.splinecode",
        fallbackSrc: "/landing/purple_fallback.png",
        title: landingContent.specializations.automation.title,
        description: landingContent.specializations.automation.description,
        seeMoreText: landingContent.specializations.automation.see_more,
      },
      contents: [
        {
          header: landingContent.specializations.automation.content_1.header,
          body: landingContent.specializations.automation.content_1.body,
        },
        {
          header: landingContent.specializations.automation.content_2.header,
          body: landingContent.specializations.automation.content_2.body,
        },
        {
          header: landingContent.specializations.automation.content_3.header,
          body: landingContent.specializations.automation.content_3.body,
        },
        {
          header: landingContent.specializations.automation.content_4.header,
          body: landingContent.specializations.automation.content_4.body,
        },
      ],
    },
    {
      colorKey: "agile",
      ctaText: landingContent.specializations.agile.cta,
      header: {
        splineCodeSrc: "/landing/green.splinecode",
        fallbackSrc: "/landing/green_fallback.png",
        title: landingContent.specializations.agile.title,
        description: landingContent.specializations.agile.description,
        seeMoreText: landingContent.specializations.agile.see_more,
      },
      contents: [
        {
          header: landingContent.specializations.agile.content_1.header,
          body: landingContent.specializations.agile.content_1.body,
        },
        {
          header: landingContent.specializations.agile.content_2.header,
          body: landingContent.specializations.agile.content_2.body,
        },
        {
          header: landingContent.specializations.agile.content_3.header,
          body: landingContent.specializations.agile.content_3.body,
        },
        {
          header: landingContent.specializations.agile.content_4.header,
          body: landingContent.specializations.agile.content_4.body,
        },
      ],
    },
  ];

  return (
    <>
      {!isTablet && <Menu fixed />}

      <HeroCard
        title="Everybody"
        subtitle="needs IT buddy"
        animationPrefix="to"
        animationWords={["code", "start", "scale", "grow"]}
        ctaLinkHref="/contact"
        ctaButtonText={landingContent.free_consultation}
        ctaButtonTextShort={landingContent.free_consultation_short}
      />

      {isTablet ? (
        <>
          <Subheader
            className="mt-4"
            content={{
              firstLine: landingContent.subheader_1,
              secondLine: landingContent.subheader_2,
            }}
          />
          <TechnologiesSection
            className="mt-4"
            header={landingContent.technology_header}
            technologies={technologies}
          />
        </>
      ) : (
        <>
          <TechnologiesSection
            className="mt-4"
            header={landingContent.technology_header}
            technologies={technologies}
          />
          <Subheader
            className="mt-4"
            content={{
              firstLine: landingContent.subheader_1,
              secondLine: landingContent.subheader_2,
            }}
          />
        </>
      )}

      <TechnologiesBrag
        className="mt-4"
        header={landingContent.technologies_brag.header}
        body={landingContent.technologies_brag.body}
      />

      <Referals className="mt-4" />

      <Values values={valuesData} />

      <CTA
        className="mt-8"
        buttonText={landingContent.cta.button}
        header={landingContent.cta.header}
        subheader={landingContent.cta.subheader}
      />

      <Specializations
        header={landingContent.specializations.header}
        subheaderParts={[
          landingContent.specializations.subheader_1,
          ", ",
          ` ${landingContent.specializations.subheader_4}`,
        ]}
        inlineItems={specializationsInlineData}
        sections={specializationsSectionsData}
        ctaBaseText={landingContent.specializations.cta}
      />

      <Process
        title={landingContent.process.title}
        subtitle={landingContent.process.subtitle}
        content={[
          landingContent.process.content_1,
          landingContent.process.content_2,
          landingContent.process.content_3,
          landingContent.process.content_4,
          landingContent.process.content_5,
          landingContent.process.content_6,
          landingContent.process.content_7,
        ]}
      />

      <CaseStudies
        header={landingContent.case_study.header}
        subheader={landingContent.case_study.subheader}
      />

      <Footer />
    </>
  );
};

interface HomeProps {
  locale: string;
}

const Home: NextPage<HomeProps> = ({ locale }) => {
  const appContext = React.useContext(AppContext);
  const { t } = useTranslation();

  return (
    <>
      <PageMetadata
        title="Your best IT buddy"
        description={t("we want to be your buddy")}
      />

      <PageBody locale={locale} />
    </>
  );
};

export async function getStaticProps({ locale }: { locale: string }) {
  return {
    props: {
      locale,
      ...(await serverSideTranslations(locale, ["common"])),
    },
  };
}

export default Home;
